import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { defaultTransition } from '../../theme'
import { Box } from '../UI/Box'
import useIsLoggedIn from '../../hooks/useIsLoggedIn'
import { toast } from 'react-toastify'
import AnimatedLoader from '../AnimatedLoader'
import ThemedButton from '../ThemedButton'
import useWeb3 from '../../hooks/useWeb3'
import { useSocialAccounts } from '@dynamic-labs/sdk-react-core'
import { ProviderEnum } from '@dynamic-labs/sdk-api-core'

const Link = styled.div`
  color: ${({ theme }) => theme.colors.brandPrimary};
  span.active {
    color: ${({ theme }) => theme.colors.brandPrimary};
    ${defaultTransition}
    :hover {
      cursor: pointer;
      color: ${({ theme }) => theme.colors.brandSecondary};
    }
  }
`

const ConnectedAccount = styled(Box)`
  flex-grow: 0;
  button {
    ${defaultTransition}
  }
`

const DiscordConnectButton = ({ label }: { label?: string }) => {
  const {
    getLinkedAccountInformation,
    linkSocialAccount,
    unlinkSocialAccount,
    isProcessing,
    isLinked,
  } = useSocialAccounts()
  const { user, isLoggedIn } = useIsLoggedIn()

  const { discordHandle: _userDiscordHandle } = user ?? { discordHandle: '' }
  const [discordHandle, setDiscordHandle] = useState(_userDiscordHandle)
  useEffect(() => {
    const acc = getLinkedAccountInformation(ProviderEnum.Discord)
    const { displayName, username } = acc ?? { displayName: '', username: '' }
    setDiscordHandle(_userDiscordHandle || displayName || username)
  }, [getLinkedAccountInformation, _userDiscordHandle])

  const { initWeb3 } = useWeb3()

  const connectDiscord = useCallback(async () => {
    try {
      await linkSocialAccount(ProviderEnum.Discord)
    } catch (err) {
      console.error(err)
      toast.error('Failed to connect Discord')
    }
  }, [linkSocialAccount])

  const disconnectDiscord = useCallback(async () => {
    try {
      await unlinkSocialAccount(ProviderEnum.Discord)
    } catch (err) {
      console.error(err)
      toast.error('There was an error disconnecting your Discord account')
    }
  }, [unlinkSocialAccount])

  if (!isLoggedIn) {
    return (
      <Link>
        <span className="active" onClick={() => initWeb3()}>
          Connect
        </span>
      </Link>
    )
  }

  if (!!isLinked(ProviderEnum.Discord)) {
    return (
      <ConnectedAccount ac g={16} className="force-row">
        {discordHandle}
        <ThemedButton
          $styleType="surface-chip"
          onClick={disconnectDiscord}
          text={isProcessing ? <AnimatedLoader size={16} /> : 'Disconnect'}
        />
      </ConnectedAccount>
    )
  }

  return (
    <Link>
      <ThemedButton
        $styleType="surface-chip"
        onClick={connectDiscord}
        text={isProcessing ? <AnimatedLoader size={16} /> : label ?? 'Connect'}
      />
    </Link>
  )
}

export default DiscordConnectButton
